// Infection control (docs/plans/phase3-operations-modules.md §2): which isolation flags
// show, the onset of a hospital-acquired infection against its stay, and the line list's
// counts. Definitions (for example how many hours after admission an infection counts as
// hospital-acquired) are not applied here: TODO(clinical-verify), plan C1.

import { err, ok, type Result } from '../result.ts';
import { compareDays, daysBetween, eatDayOf, parseIsoDay } from '../time.ts';

export const ISOLATION_FLAG_STATUSES = ['active', 'ended', 'entered_in_error'] as const;
export type IsolationFlagStatus = (typeof ISOLATION_FLAG_STATUSES)[number];

export const ISOLATION_FLAG_STATUS_LABELS: Record<IsolationFlagStatus, string> = {
  active: 'In place',
  ended: 'Ended',
  entered_in_error: 'Entered in error',
};

export const INFECTION_TEXT_LIMITS = { note: 1000, organism: 120 } as const;

/**
 * Whether a flag shows on the banner and the bed board: an active patient flag until ended;
 * an active visit flag only while that visit is open. Passive: it never blocks anything.
 */
export function flagShows(flag: {
  status: IsolationFlagStatus;
  /** The linked visit's status, or null for a flag on the patient. */
  visitStatus: string | null;
}): boolean {
  if (flag.status !== 'active') return false;
  return flag.visitStatus === null || flag.visitStatus === 'open';
}

/** "Contact precautions" from the list's "Contact"; a display already saying so is kept. */
export function precautionWords(display: string): string {
  return /precautions?$/i.test(display.trim()) ? display.trim() : `${display.trim()} precautions`;
}

/** Whole East Africa Time days from the admission day (day 0) to the onset day. */
export function daysFromAdmissionToOnset(admittedAt: Date, onsetOn: string): number {
  const onset = parseIsoDay(onsetOn);
  if (!onset.ok) throw new RangeError('onset day is not a date');
  return daysBetween(eatDayOf(admittedAt), onset.value);
}

export type InfectionErrorCode =
  'infection.onset_in_future' | 'infection.onset_before_admission' | 'infection.onset_invalid';

export const INFECTION_ERROR_MESSAGES: Record<InfectionErrorCode, string> = {
  'infection.onset_in_future': 'The onset day is in the future. Choose the day signs began.',
  'infection.onset_before_admission':
    'The onset day is before the admission. An infection present on admission is not recorded here.',
  'infection.onset_invalid': 'Enter the onset day as a date.',
};

/** The onset day is within the stay so far: not before the admission day, not after today. */
export function checkInfectionOnset(
  input: { onsetOn: string; admittedAt: Date },
  now: Date,
): Result<string, InfectionErrorCode> {
  const onset = parseIsoDay(input.onsetOn);
  if (!onset.ok) return err('infection.onset_invalid');
  if (compareDays(onset.value, eatDayOf(now)) > 0) return err('infection.onset_in_future');
  if (compareDays(onset.value, eatDayOf(input.admittedAt)) < 0)
    return err('infection.onset_before_admission');
  return ok(input.onsetOn);
}

export interface CountRow {
  key: string;
  label: string;
  count: number;
}

function countRows(entries: readonly { key: string; label: string }[]): CountRow[] {
  const map = new Map<string, CountRow>();
  for (const { key, label } of entries) {
    const row = map.get(key);
    if (row === undefined) map.set(key, { key, label, count: 1 });
    else row.count += 1;
  }
  return [...map.values()].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.key === '') return 1;
    if (b.key === '') return -1;
    return a.label.localeCompare(b.label);
  });
}

/** The line list's counts: by category and by ward at onset, most first. */
export function infectionCounts(
  events: readonly { category: { code: string; display: string }; wardName: string | null }[],
): { total: number; byCategory: CountRow[]; byWard: CountRow[] } {
  return {
    total: events.length,
    byCategory: countRows(events.map((e) => ({ key: e.category.code, label: e.category.display }))),
    byWard: countRows(
      events.map((e) => ({ key: e.wardName ?? '', label: e.wardName ?? 'Ward not recorded' })),
    ),
  };
}
